import { Option } from "@models/clients/option";
import { ClientCRMRepo } from "@services/clients/clientCRM.repo";

interface OptionEditModalBindings {
  optionType: {
    type: string;
    title: string;
    icon: string;
  };
}

class OptionEditModalController implements angular.IOnInit, angular.IOnDestroy {
  public options: Option[] = [];
  public loading = false;
  public saving = false;
  public newOption: Option = { id: 0, value: '', displayName: '' };
  public isAddPanelOpen = false;
  public isEditMode = false;
  public errorMessage = '';

  public readonly columns = Object.freeze([
    { key: 'displayName', label: 'Display Name', width: 'col-md-8' },
  ]);

  public optionType: {
    type: string;
    title: string;
    icon: string;
  };

  /** @ngInject */
  constructor(
    private clientCRMRepo: ClientCRMRepo,
    private $mdDialog: angular.material.IDialogService
  ) {}

  async $onInit() {
    if (this.optionType && this.optionType.type) {
      await this.loadOptions();
    } else {
      console.error('OptionType not properly initialized, received:', this.optionType);
    }
  }

  private async loadOptions() {
    if (!this.optionType || !this.optionType.type) {
      console.error('Cannot load options: optionType is not defined');
      return;
    }

    try {
      this.loading = true;
      const response = await this.clientCRMRepo.getOptions(this.optionType.type);
      this.options = response.data as Option[];
    } catch (error) {
      console.error(`Error loading ${this.optionType.type} options:`, error);
      this.options = [];
    } finally {
      this.loading = false;
    }
  }

  public refresh() {
    this.loadOptions();
  }

  public toggleAddPanel() {
    this.isEditMode = false;
    this.isAddPanelOpen = true;
    this.newOption = { id: 0, value: '', displayName: '' };
    this.errorMessage = '';
    document.body.classList.add('slide-out-panel-open');
  }

  public editOption(item: Option) {
    this.isEditMode = true;
    this.isAddPanelOpen = true;
    this.newOption = { ...item };
    this.errorMessage = '';
    document.body.classList.add('slide-out-panel-open');
  }

  public closeAddPanel() {
    this.isAddPanelOpen = false;
    this.isEditMode = false;
    this.errorMessage = '';
    this.newOption = { id: 0, value: '', displayName: '' };
  }

  public async saveOption() {
    console.log(`${this.isEditMode ? 'Updating' : 'Saving'} option`, this.newOption);
    if (!this.newOption.displayName.trim()) {
      this.errorMessage = 'Display name is required';
      return;
    }

    if (!this.optionType || !this.optionType.type) {
      console.error('Cannot save option: optionType is not defined');
      this.errorMessage = 'Option type is not defined';
      return;
    }

    try {
      this.saving = true;
      this.errorMessage = '';
      
      // Generate value from display name if not provided
      if (!this.newOption.value || !this.newOption.value.trim()) {
        // this.newOption.value = this.newOption.displayName.toLowerCase().replace(/\s+/g, '_');
        this.newOption.value = null;
      }

      const response = await this.clientCRMRepo.saveOption(this.optionType.type, this.newOption);
      
      if (response.status === 200) {
        this.closeAddPanel();
        await this.loadOptions();
      }
    } catch (error) {
      console.error(`Error ${this.isEditMode ? 'updating' : 'saving'} option:`, error);
      this.errorMessage = `Error ${this.isEditMode ? 'updating' : 'saving'} option. Please try again.`;
    } finally {
      this.saving = false;
    }
  }

  public deleteSelectedOptions(itemIds: any[]): angular.IPromise<void> | void {
    if (!itemIds || itemIds.length === 0) {
      return;
    }

    if (!this.optionType || !this.optionType.type) {
      console.error('Cannot delete options: optionType is not defined');
      return;
    }

    this.saving = true;
    return this.clientCRMRepo.deleteOptions(this.optionType.type, itemIds)
      .then((response: any) => {
        if (response && response.status === 200) {
          return this.loadOptions();
        }
      })
      .catch((error) => {
        console.error('Error deleting options:', error);
        throw error;
      })
      .finally(() => {
        this.saving = false;
      });
  }

  public saveChanges() {
    // This method is called when the modal's save button is clicked
    // For now, we'll just close the modal since individual changes are saved immediately
    this.$mdDialog.hide(this.options);
  }

  public cancel() {
    this.$mdDialog.cancel();
  }

  $onDestroy() {
    document.body.classList.remove('slide-out-panel-open');
  }
}

export const optionEditModal = {
  template: require("./option-edit-modal.html"),
  style: require("./option-edit-modal.scss"),
  controller: OptionEditModalController,
  controllerAs: '$ctrl',
  bindings: {
    optionType: "<?"
  }
};



// WEBPACK FOOTER //
// ./src/components/option-edit-modal/option-edit-modal.ts